import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentDots,
  faSpinner,
  faTrash,
  faEye,
  faImage,
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faExpand,
  faCompress
} from '@fortawesome/free-solid-svg-icons';
import ConfirmDialog from './ConfirmDialog';
import ZoomableImage from './ZoomableImage';
import '../styles/FeedbackTab.css';

const API_BASE_URL = window.API_BASE_URL;

export default function FeedbackTab({ currentUser }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [images, setImages] = useState([]);
  const [imageIdx, setImageIdx] = useState(0);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });

  const canDeleteAny = currentUser?.role === 'examiner' || currentUser?.role === 'administrator';

  const loadPacks = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load feedback');
      }
      setPacks(data.packs || []);
    } catch (err) {
      console.error('Error loading feedback:', err);
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPacks();
  }, []);

  const classSortKey = (r) => {
    const cls = [r.class, r.section].filter(Boolean).join('-');
    return cls + (r.subject ? ` · ${r.subject}` : '');
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: column === 'date' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (column) => (
    <span className={`feedback-sort-ind${sortConfig.column === column ? ' active' : ''}`}>
      {sortConfig.column === column ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  const sorted = useMemo(() => {
    const list = [...packs];
    if (!sortConfig.column) return list;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      if (sortConfig.column === 'teacher') {
        cmp = String(a.created_by || '').localeCompare(String(b.created_by || ''), undefined, {
          sensitivity: 'base'
        });
      } else if (sortConfig.column === 'class') {
        cmp = classSortKey(a).localeCompare(classSortKey(b), undefined, {
          sensitivity: 'base',
          numeric: true
        });
      } else if (sortConfig.column === 'student') {
        cmp = String(a.student_name || '').localeCompare(String(b.student_name || ''), undefined, {
          sensitivity: 'base'
        });
      } else if (sortConfig.column === 'date') {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        cmp = aTime - bTime;
      }
      return cmp * dir;
    });
    return list;
  }, [packs, sortConfig]);

  const selectedIndex = useMemo(
    () => (selected ? sorted.findIndex((p) => p.id === selected.id) : -1),
    [sorted, selected]
  );

  const openDetail = async (pack) => {
    setSelected(pack);
    setImages([]);
    setImageIdx(0);
    setPresenting(false);
    setError('');
    try {
      setImagesLoading(true);
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/feedback/${pack.id}/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.images)) {
        setImages(data.images);
        setImageIdx(0);
      }
    } catch (err) {
      console.error('Error loading feedback images:', err);
      setError(err.message || 'Failed to load images');
    } finally {
      setImagesLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setImages([]);
    setImageIdx(0);
    setPresenting(false);
  };

  const goToPackOffset = useCallback((delta) => {
    if (selectedIndex < 0) return;
    const next = selectedIndex + delta;
    if (next < 0 || next >= sorted.length) return;
    openDetail(sorted[next]);
  }, [selectedIndex, sorted]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToImageOffset = useCallback((delta) => {
    setImageIdx((prev) => {
      const next = prev + delta;
      if (next < 0 || next >= images.length) return prev;
      return next;
    });
  }, [images.length]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (presenting) setPresenting(false);
        else closeDetail();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToImageOffset(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToImageOffset(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goToPackOffset(-1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goToPackOffset(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, presenting, goToImageOffset, goToPackOffset]);

  const handleDelete = (pack) => {
    setConfirm({
      show: true,
      text: `Delete feedback for ${pack.student_name}? The annotated screenshots will be removed.`,
      onConfirm: async () => {
        setConfirm({ show: false, text: '', onConfirm: null });
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/feedback/${pack.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to delete feedback');
          }
          if (selected?.id === pack.id) closeDetail();
          setPacks((prev) => prev.filter((p) => p.id !== pack.id));
        } catch (err) {
          setError(err.message || 'Failed to delete feedback');
        }
      }
    });
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      return new Date(val).toLocaleString();
    } catch {
      return String(val);
    }
  };

  const formatTestDate = (val) => {
    if (!val) return '—';
    try {
      const raw = typeof val === 'string' ? val.split('T')[0] : null;
      const d = raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)
        ? new Date(`${raw}T00:00:00`)
        : new Date(val);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString();
    } catch {
      return String(val);
    }
  };

  const currentImage = images[imageIdx] || null;
  const canDeleteThis = selected && (
    canDeleteAny || selected.created_by === currentUser?.username
  );

  if (selected) {
    return (
      <div className={`feedback-tab${presenting ? ' presenting' : ''}`}>
        <div className="feedback-detail-page">
          {!presenting && (
            <div className="feedback-detail-left">
              <div className="feedback-detail-left-header">
                <button type="button" className="feedback-back-btn" onClick={closeDetail}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Back
                </button>
                <h2>{selected.student_name}</h2>
              </div>
              <div className="feedback-detail-scroll">
                {error && (
                  <div className="feedback-alert error" onClick={() => setError('')}>
                    {error}
                  </div>
                )}
                <div className="feedback-meta-grid">
                  <div><span>Class</span><strong>{[selected.class, selected.section].filter(Boolean).join('-') || '—'}</strong></div>
                  <div><span>Subject</span><strong>{selected.subject || '—'}</strong></div>
                  <div><span>Marks</span><strong>{selected.original_marks != null && selected.original_marks !== '' ? selected.original_marks : '—'}</strong></div>
                  <div><span>Test date</span><strong>{formatTestDate(selected.test_date)}</strong></div>
                  <div><span>Teacher</span><strong>{selected.created_by || '—'}</strong></div>
                  <div><span>Sent</span><strong>{formatDate(selected.created_at)}</strong></div>
                  <div><span>Images</span><strong>{images.length || selected.image_count || 0}</strong></div>
                </div>
                {selected.note ? (
                  <div className="feedback-section">
                    <h4>Note</h4>
                    <p className="feedback-note">{selected.note}</p>
                  </div>
                ) : (
                  <p className="feedback-muted">No teacher note — show the annotated image(s).</p>
                )}
                <div className="feedback-detail-actions">
                  <button
                    type="button"
                    className="feedback-btn present"
                    onClick={() => setPresenting(true)}
                    title="Full-screen for showing the class"
                  >
                    <FontAwesomeIcon icon={faExpand} /> Present
                  </button>
                  <div className="feedback-pack-nav">
                    <button
                      type="button"
                      className="feedback-btn ghost"
                      disabled={selectedIndex <= 0}
                      onClick={() => goToPackOffset(-1)}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} /> Prev student
                    </button>
                    <button
                      type="button"
                      className="feedback-btn ghost"
                      disabled={selectedIndex < 0 || selectedIndex >= sorted.length - 1}
                      onClick={() => goToPackOffset(1)}
                    >
                      Next student <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                  {canDeleteThis && (
                    <button
                      type="button"
                      className="feedback-btn danger"
                      onClick={() => handleDelete(selected)}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="feedback-detail-right">
            <div className="feedback-detail-right-header">
              {presenting && (
                <button type="button" className="feedback-present-exit" onClick={() => setPresenting(false)}>
                  <FontAwesomeIcon icon={faCompress} /> Exit present
                </button>
              )}
              <FontAwesomeIcon icon={faImage} />
              <span>
                {selected.student_name}
                {images.length > 1 ? ` · ${imageIdx + 1} / ${images.length}` : ''}
              </span>
            </div>
            <div className="feedback-detail-image-stage">
              {imagesLoading ? (
                <div className="feedback-loading" style={{ color: '#e5e7eb' }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading images…
                </div>
              ) : currentImage ? (
                <>
                  <ZoomableImage
                    url={currentImage.downloadUrl}
                    alt={currentImage.fileName || selected.student_name}
                    width="100%"
                    height="100%"
                    background="#111827"
                  />
                  {images.length > 1 && (
                    <div className="feedback-image-nav">
                      <button
                        type="button"
                        disabled={imageIdx === 0}
                        onClick={() => goToImageOffset(-1)}
                      >
                        ‹
                      </button>
                      <span>{imageIdx + 1} / {images.length}</span>
                      <button
                        type="button"
                        disabled={imageIdx >= images.length - 1}
                        onClick={() => goToImageOffset(1)}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="feedback-empty" style={{ color: '#e5e7eb' }}>
                  No annotated images on this pack.
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmDialog
          confirm={confirm}
          onConfirm={() => {
            const fn = confirm.onConfirm;
            setConfirm({ show: false, text: '', onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() => setConfirm({ show: false, text: '', onConfirm: null })}
        />
      </div>
    );
  }

  return (
    <div className="feedback-tab">
      <div className="feedback-header">
        <div>
          <h2>
            <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: 10, color: '#0D9488' }} />
            Feedback
          </h2>
          <p>
            Annotated screenshots sent from Scans. Open a student to show the class their marked work.
          </p>
        </div>
      </div>

      {error && (
        <div className="feedback-alert error" onClick={() => setError('')}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="feedback-loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading feedback…
        </div>
      ) : sorted.length === 0 ? (
        <div className="feedback-empty">
          No feedback yet. In Scans, Screenshot → annotate → Save, then Send to Feedback.
        </div>
      ) : (
        <div className="feedback-table-wrap">
          <table className="feedback-table">
            <thead>
              <tr>
                <th
                  className="feedback-th-sortable"
                  onClick={() => handleSort('student')}
                >
                  Student{sortIndicator('student')}
                </th>
                <th
                  className="feedback-th-sortable"
                  onClick={() => handleSort('class')}
                >
                  Class / Subject{sortIndicator('class')}
                </th>
                <th>Marks</th>
                <th>Test date</th>
                <th
                  className="feedback-th-sortable"
                  onClick={() => handleSort('teacher')}
                >
                  Sent by{sortIndicator('teacher')}
                </th>
                <th>Images</th>
                <th
                  className="feedback-th-sortable"
                  onClick={() => handleSort('date')}
                >
                  Date{sortIndicator('date')}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((pack) => (
                <tr key={pack.id}>
                  <td>
                    <button
                      type="button"
                      className="feedback-student-link"
                      onClick={() => openDetail(pack)}
                    >
                      {pack.student_name}
                    </button>
                  </td>
                  <td>
                    {[pack.class, pack.section].filter(Boolean).join('-') || '—'}
                    {pack.subject ? ` · ${pack.subject}` : ''}
                  </td>
                  <td>{pack.original_marks != null && pack.original_marks !== '' ? pack.original_marks : '—'}</td>
                  <td>{formatTestDate(pack.test_date)}</td>
                  <td>{pack.created_by || '—'}</td>
                  <td>
                    <span className="feedback-image-count">
                      <FontAwesomeIcon icon={faImage} /> {pack.image_count || 1}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatDate(pack.created_at)}</td>
                  <td>
                    <div className="feedback-row-actions">
                      <button
                        className="feedback-btn view"
                        onClick={() => openDetail(pack)}
                      >
                        <FontAwesomeIcon icon={faEye} /> Show
                      </button>
                      {(canDeleteAny || pack.created_by === currentUser?.username) && (
                        <button
                          className="feedback-btn danger"
                          onClick={() => handleDelete(pack)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        confirm={confirm}
        onConfirm={() => {
          const fn = confirm.onConfirm;
          setConfirm({ show: false, text: '', onConfirm: null });
          if (fn) fn();
        }}
        onCancel={() => setConfirm({ show: false, text: '', onConfirm: null })}
      />
    </div>
  );
}
